import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Modal } from 'react-bootstrap';
import moment from 'moment-timezone';
import classNames from 'classnames';
import SplitShiftSegmentRow from './SplitShiftSegmentRow';
import ArrowSplitIconWhite from '../../../assets/images/arrow-split-white.svg';
import ArrowSplitIconPurple from '../../../assets/images/arrow-split-purple.svg';
import InfoIconGray from '../../../assets/images/info-icon-gray.svg';
import {
	useSplitShiftMutation,
	useGetSlotSegmentsQuery,
	useUpdateSegmentUnitsMutation,
	invalidateSlotSegments
} from '../../../redux/globalOverview';
import { showNotification } from '../../../redux/notification';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import {
	defaultSegments,
	sortSegments,
	canDeleteSegment,
	reanchorBoundaries,
	validateSegments,
	formatRange,
	toPayload,
	buildDiffRows,
	cancellationChanged,
	MIDDLE_CANCELLATION_MESSAGE,
	resolveUnitName,
	INVALID_TIME_MESSAGE,
	NO_UNIT_LABEL
} from './segmentUtils';

import './SplitShift.scss';
import { loadUser, user, facilitiesEnabled, splitShiftsMaxSegments } from '../../../redux/user';
import { ArrowIcon } from '../../../components/icons';
import utils from '../../../utils/utils';
import PERMISSION from '../../../constants/Permissions';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';
import xhr from '../../../utils/xhr';

const REMOVE_UNIT_OPTION = { label: '- Remove Unit -', value: null };
const UPDATE_FAILED_MESSAGE = 'The shift could not be updated as it is no longer active. Please try again.';
const CANCEL_PERMISSION_MESSAGE = 'The shift could not be cancelled as you do not have permission.';
const EDIT_PERMISSION_MESSAGE = 'The shift could not be updated as you do not have permission.';
const SHIFT_REFRESHED_MESSAGE = 'The shift has been updated and the page was refreshed.';

/**
 * Decide how the modal reacts to a failed split/edit request.
 *
 * @param {{ status?: number|string, originalStatus?: number, data?: { errors?: Array<object> } }} error RTK rejection.
 * @param {boolean} isEditing Whether the slot was already split before this request.
 * @param {boolean} willCancelSlot Whether the request cancels every segment, and so the slot.
 * @returns {{ fieldErrors?: Object<string, string>, message?: string, keepOpen: boolean, reloadUser?: boolean }}
 */
const getConfirmErrorOutcome = (error, isEditing, willCancelSlot) => {
	// The route guard answers a missing Add/Edit Flex Shifts permission with a bare 403 ("Forbidden"),
	// which RTK Query reports as a parsing error carrying originalStatus.
	if (isEditing && (error?.status === 403 || error?.originalStatus === 403)) {
		return { message: EDIT_PERMISSION_MESSAGE, keepOpen: false };
	}
	const apiErrors = Array.isArray(error?.data?.errors) ? error.data.errors : [];
	// Segment-level errors are surfaced back on the edit step next to the failing picker.
	const fieldErrors = {};
	apiErrors.forEach((item) => {
		if (item.field?.startsWith('segments[')) {
			fieldErrors[item.field] = item.message;
		}
	});
	if (Object.keys(fieldErrors).length > 0) {
		return { fieldErrors, keepOpen: true };
	}

	const [firstError = {}] = apiErrors;
	// Cancelling every segment cancels the slot, which needs PORTAL.FLEX.CANCEL (and the past-shift
	// permission once the shift has ended); the generic fallback would wrongly say the shift is inactive.
	if (willCancelSlot && firstError.field === 'permissions') {
		return { message: CANCEL_PERMISSION_MESSAGE, keepOpen: false };
	}
	switch (firstError.field) {
		// The whole-list `segments` error is the configured max-segments cap. Keep the modal open on the edit
		// step so the user can act on the toast and remove segments.
		case 'segments':
			return { message: firstError.message, keepOpen: true };
		// PC-6 from the server: reachable through a concurrent edit or a direct API client even
		// though client validation catches it first. Same red toast and same open modal as the
		// client-side path — the generic fallback would wrongly say the shift is inactive.
		case 'cancellation_order':
			return { message: firstError.message || MIDDLE_CANCELLATION_MESSAGE, keepOpen: true };
		// The split was already reverted elsewhere, so the shift is still active but no longer split.
		case 'revert_state':
			return { message: firstError.message, keepOpen: false };
		default: {
			const message = isEditing ? UPDATE_FAILED_MESSAGE : firstError.message || UPDATE_FAILED_MESSAGE;
			// Unit permissions changed under the user; refresh them before closing.
			return { message, keepOpen: false, reloadUser: firstError.field === 'unit_id' };
		}
	}
};

const SplitShiftModal = ({ show, onHide, slot, units = [], unitsOnly = false }) => {
	const userFacilities = useSelector(facilitiesEnabled, shallowEqual);
	const { permissionsByFacility, managementCompanyPermissions } = useSelector(user, shallowEqual);
	const maxSegments = useSelector(splitShiftsMaxSegments);
	const shiftStart = useMemo(() => moment.tz(slot.shift_start, slot.facility_timezone), [
		slot.shift_start,
		slot.facility_timezone
	]);
	const shiftEnd = useMemo(() => moment.tz(slot.shift_end, slot.facility_timezone), [
		slot.shift_end,
		slot.facility_timezone
	]);
	const rowIdRef = useRef(0);

	const shouldShowUnits = useMemo(() => {
		const facility = userFacilities.find(
			(userFacility) => parseInt(userFacility.id, 10) === parseInt(slot.facility_id, 10)
		);
		 
		return !!facility?.portal_units_enabled;
	}, [userFacilities, slot.facility_id]);

	// Facility-scoped cancel permission, derived here rather than passed in: both call sites already
	// hand us `slot`, and the modal is the only consumer. Cancelling a shift that has already ended
	// additionally requires the management-company Add/Edit Past Flex Shifts permission — mirroring
	// the server gate. Splitting, retiming and unit changes in the past are not affected.
	const canCancel = useMemo(() => {
		if (!utils.isAuthorizedExact(PERMISSION.FLEX_CANCEL, permissionsByFacility[slot.facility_id] || [])) {
			return false;
		}
		if (!shiftEnd.isBefore(moment())) {
			return true;
		}
		return utils.isAuthorizedExact(
			MANAGEMENT_COMPANIES_PERMISSIONS.PORTAL_CREATE_FLEX_PAST_SHIFTS,
			(managementCompanyPermissions && managementCompanyPermissions[slot.management_company_id]) || []
		);
	}, [permissionsByFacility, slot.facility_id, shiftEnd, managementCompanyPermissions, slot.management_company_id]);

	const isEditing = !!slot.has_segments;
	const { data: existingSegments, isFetching: isLoadingSegments } = useGetSlotSegmentsQuery(
		{ slotId: slot.slot_id, managementCompanyId: slot.management_company_id },
		{ skip: !show || !isEditing, refetchOnMountOrArgChange: true }
	);

	const buildSegments = useCallback(() => {
		rowIdRef.current = 0;
		const source = isEditing && existingSegments ? existingSegments : null;
		if (!source) {
			return defaultSegments(shiftStart.clone(), shiftEnd.clone()).map((segment) => {
				rowIdRef.current += 1;
				return { ...segment, isCancelled: false, cancelationReason: null, rowId: rowIdRef.current };
			});
		}
		return source.map((segment) => {
			const {
				start_time: startTime,
				end_time: endTime,
				unit_id: unitId,
				segment_number: segmentNumber,
				cancelation_reason: cancelationReason
			} = segment;
			rowIdRef.current += 1;
			return {
				start: moment.tz(startTime, slot.facility_timezone),
				end: moment.tz(endTime, slot.facility_timezone),
				unitId: unitId ? parseInt(unitId, 10) : null,
				segmentNumber,
				isCancelled: !!cancelationReason,
				cancelationReason: cancelationReason || null,
				rowId: rowIdRef.current
			};
		});
	}, [isEditing, existingSegments, shiftStart, shiftEnd, slot.facility_timezone]);

	const [segments, setSegments] = useState(buildSegments);
	const [errors, setErrors] = useState({});
	const [isConfirming, setIsConfirming] = useState(false);
	const [cancelationReasonOptions, setCancelationReasonOptions] = useState([]);
	const [splitShift, { isLoading: isSplitting }] = useSplitShiftMutation();
	const [updateSegmentUnits, { isLoading: isUpdatingUnits }] = useUpdateSegmentUnitsMutation();
	const isLoading = isSplitting || isUpdatingUnits;
	const dispatch = useDispatch();

	useEffect(() => {
		setSegments(buildSegments());
		setErrors({});
		setIsConfirming(false);
	}, [buildSegments]);

	// PC-9: the fixed flex cancellation pick list, fetched once per open. The endpoint already
	// returns `{label, value}` with the reason code as the value, which is what crosses the wire.
	useEffect(() => {
		if (!show) {
			return;
		}
		const getPortalCancellationReasons = async () => {
			try {
				const { data } = await xhr.request('GET', `/entity/slot-cancelation-reasons?filterQuery=FLEX_SLOTS`);
				setCancelationReasonOptions(data);
			} catch {
				dispatch(showNotification('error', 'Failed to fetch portal cancellation reasons.'));
			}
		};
		getPortalCancellationReasons();
	}, [show, dispatch]);

	const hasNoSegmentsToEdit =
		isEditing && show && !isLoadingSegments && Array.isArray(existingSegments) && existingSegments.length === 0;
	useEffect(() => {
		if (hasNoSegmentsToEdit) {
			dispatch(invalidateSlotSegments(slot.slot_id));
			dispatch(showNotification('error', SHIFT_REFRESHED_MESSAGE));
			onHide(true);
		}
	}, [hasNoSegmentsToEdit, dispatch, onHide, slot.slot_id]);

	// Number() because facility_units.id is a bigint and reaches us as a string from the Daily
	// Schedule but as a number from the Global Overview. Segment unitIds are always numbers, and
	// the Select matches its value against option values with ===, so both sides must be numeric.
	const unitOptions = useMemo(
		() => [REMOVE_UNIT_OPTION, ...units.map((unit) => ({ label: unit.unit_name, value: Number(unit.id) }))],
		[units]
	);
	const sortedSegments = useMemo(() => sortSegments(segments), [segments]);
	const unitsPayload = useMemo(
		() => sortedSegments.map((segment) => ({ segmentNumber: segment.segmentNumber, unitId: segment.unitId || null })),
		[sortedSegments]
	);

	const confirmationRows = useMemo(
		() =>
			sortedSegments.map((segment, index) => ({
				segmentNumber: index + 1,
				time: formatRange(segment),
				unit: resolveUnitName(units, segment.unitId),
				isCancelled: !!segment.isCancelled
			})),
		[sortedSegments, units]
	);

	// PC-10: the server cancels the slot when every segment it stores carries a reason, so the
	// modal reads the same condition off the submitted rows. Never in unit-only mode — those rows seed
	// isCancelled from what is already stored, and the save there goes to updateSegmentUnits, which
	// cancels nothing.
	const willCancelSlot = useMemo(
		() => !unitsOnly && sortedSegments.length > 0 && sortedSegments.every((segment) => segment.isCancelled),
		[unitsOnly, sortedSegments]
	);

	const originalSegments = useMemo(() => {
		if (!isEditing || !existingSegments) {
			return [];
		}
		return existingSegments.map((segment) => {
			const {
				start_time: startTime,
				end_time: endTime,
				unit_id: unitId,
				cancelation_reason: cancelationReason
			} = segment;
			return {
				start: moment.tz(startTime, slot.facility_timezone),
				end: moment.tz(endTime, slot.facility_timezone),
				unitId: unitId ? parseInt(unitId, 10) : null,
				cancelationReason: cancelationReason || null
			};
		});
	}, [isEditing, existingSegments, slot.facility_timezone]);

	const diffRows = useMemo(() => (isEditing ? buildDiffRows(originalSegments, sortedSegments, units) : []), [
		isEditing,
		originalSegments,
		sortedSegments,
		units
	]);

	// `buildDiffRows` does not look at cancellation, so a cancel-only edit would leave Save disabled.
	const hasChanges = useMemo(
		() => diffRows.some((row) => row.status !== 'unchanged') || cancellationChanged(originalSegments, sortedSegments),
		[diffRows, originalSegments, sortedSegments]
	);

	const setSegmentTimeError = (index, field) => {
		setErrors((prev) => ({ ...prev, [`segments[${index}].${field}Time`]: INVALID_TIME_MESSAGE }));
	};

	const handleStartChange = (index, value) => {
		const previousSegment = segments[index - 1];
		const fillPreviousEnd = value && previousSegment && (!previousSegment.end || !previousSegment.end.isValid());
		setSegments(
			segments.map((segment, i) => {
				if (i === index) {
					return { ...segment, start: value };
				}
				if (i === index - 1 && fillPreviousEnd) {
					return { ...segment, end: value.clone() };
				}
				return segment;
			})
		);
		setErrors((prev) => {
			const next = { ...prev };
			delete next[`segments[${index}].startTime`];
			const previousEndKey = `segments[${index - 1}].endTime`;
			// The previous row's "Enter a valid time" flag refers to unparseable text still sitting in its
			// picker, which this edit didn't touch — keep it. The exception is the auto-fill above, which
			// replaces that picker's value (and its displayed text) with a valid time.
			if (fillPreviousEnd || next[previousEndKey] !== INVALID_TIME_MESSAGE) {
				delete next[previousEndKey];
			}
			return next;
		});
	};

	const handleEndChange = (index, value) => {
		const nextSegment = segments[index + 1];
		const fillNextStart = value && nextSegment && (!nextSegment.start || !nextSegment.start.isValid());
		setSegments(
			segments.map((segment, i) => {
				if (i === index) {
					return { ...segment, end: value };
				}
				if (i === index + 1 && fillNextStart) {
					return { ...segment, start: value.clone() };
				}
				return segment;
			})
		);
		setErrors((prev) => {
			const next = { ...prev };
			delete next[`segments[${index}].endTime`];
			const nextStartKey = `segments[${index + 1}].startTime`;
			// The next row's "Enter a valid time" flag refers to unparseable text still sitting in its
			// picker, which this edit didn't touch — keep it. The exception is the auto-fill above, which
			// replaces that picker's value (and its displayed text) with a valid time.
			if (fillNextStart || next[nextStartKey] !== INVALID_TIME_MESSAGE) {
				delete next[nextStartKey];
			}
			return next;
		});
	};
	const handleStartError = (index) => setSegmentTimeError(index, 'start');
	const handleEndError = (index) => setSegmentTimeError(index, 'end');

	const handleUnitChange = (index, { unitId }) => {
		const updated = segments.map((segment, i) => (i === index ? { ...segment, unitId: unitId || null } : segment));
		setSegments(updated);
	};

	// PC-7: every toggle clears the reason. Un-ticking returns the segment to active with no reason,
	// and re-ticking comes up empty because nothing was kept to restore.
	const handleCancelToggle = (index, isChecked) => {
		setSegments((previous) =>
			previous.map((segment, i) =>
				i === index ? { ...segment, isCancelled: isChecked, cancelationReason: null } : segment
			)
		);
		setErrors((previous) => {
			const next = { ...previous };
			delete next[`segments[${index}].cancelationReason`];
			return next;
		});
	};

	const handleCancelationReasonChange = (index, code) => {
		setSegments((previous) =>
			previous.map((segment, i) => (i === index ? { ...segment, cancelationReason: code } : segment))
		);
		setErrors((previous) => {
			const next = { ...previous };
			delete next[`segments[${index}].cancelationReason`];
			return next;
		});
	};

	const handleAddSegment = () => {
		rowIdRef.current += 1;
		// Insert the new segment just before the last one (first and last stay fixed).
		// Pre-fill its start with the preceding segment's end (the contiguous boundary)
		// and leave its end blank for the user to fill.
		const insertIndex = segments.length - 1;
		const precedingSegment = segments[insertIndex - 1];
		const newSegment = {
			start: precedingSegment && precedingSegment.end ? precedingSegment.end.clone() : null,
			end: null,
			unitId: null,
			isCancelled: false,
			cancelationReason: null,
			rowId: rowIdRef.current
		};
		// The last segment's start was the old boundary; the new segment now sits before it,
		// so re-anchor the last segment's start to the new segment's end.
		const lastSegment = segments[insertIndex];
		const updatedLast = { ...lastSegment, start: newSegment.end ? newSegment.end.clone() : null };
		// Adding back onto a single segment puts the new row at index 0, so re-anchor to keep the
		// shift start on the first row and the shift end on the last.
		setSegments(reanchorBoundaries([...segments.slice(0, insertIndex), newSegment, updatedLast], shiftStart, shiftEnd));
	};

	const handleDeleteSegment = (index) => {
		const remaining = segments.filter((segment, i) => i !== index);
		// Error keys are index-based, so re-check the remaining pickers while re-indexing them:
		// carry each surviving row's invalid-time flag over to its new index and drop everything else.
		const nextErrors = {};
		remaining.forEach((segment, newIndex) => {
			const previousIndex = newIndex >= index ? newIndex + 1 : newIndex;
			const isNewLast = newIndex === remaining.length - 1;
			if (errors[`segments[${previousIndex}].startTime`] === INVALID_TIME_MESSAGE) {
				nextErrors[`segments[${newIndex}].startTime`] = INVALID_TIME_MESSAGE;
			}
			// The new last segment's end is re-anchored to the shift end below (and its picker is
			// disabled), so any invalid-time flag it carried no longer applies.
			if (!isNewLast && errors[`segments[${previousIndex}].endTime`] === INVALID_TIME_MESSAGE) {
				nextErrors[`segments[${newIndex}].endTime`] = INVALID_TIME_MESSAGE;
			}
			if (errors[`segments[${previousIndex}].cancelationReason`]) {
				nextErrors[`segments[${newIndex}].cancelationReason`] = errors[`segments[${previousIndex}].cancelationReason`];
			}
		});
		setSegments(reanchorBoundaries(remaining, shiftStart, shiftEnd));
		setErrors(nextErrors);
	};
	const handleSplit = () => {
		const { isValid, errors: validationErrors, rangeError } = validateSegments(segments, shiftStart, shiftEnd);
		const invalidTimeErrors = {};
		Object.keys(errors).forEach((key) => {
			if (errors[key] === INVALID_TIME_MESSAGE) {
				invalidTimeErrors[key] = INVALID_TIME_MESSAGE;
			}
		});
		setErrors({ ...validationErrors, ...invalidTimeErrors });
		if (rangeError) {
			dispatch(showNotification('error', rangeError));
		}
		if (!isValid || Object.keys(invalidTimeErrors).length > 0) {
			return;
		}
		setIsConfirming(true);
	};

	const handleGoBack = () => setIsConfirming(false);

	const handleConfirm = async () => {
		try {
			if (unitsOnly) {
				await updateSegmentUnits({ slot, segments: unitsPayload }).unwrap();
			} else {
				await splitShift({ slot, segments: toPayload(sortedSegments) }).unwrap();
			}
			dispatch(showNotification('success', isEditing ? 'Shift updated successfully.' : 'Shift split successfully.'));
			onHide(true);
		} catch (error) {
			const { fieldErrors, message, keepOpen, reloadUser } = getConfirmErrorOutcome(error, isEditing, willCancelSlot);
			if (reloadUser) {
				dispatch(loadUser());
			}
			if (fieldErrors) {
				setErrors(fieldErrors);
			} else {
				dispatch(showNotification('error', message));
			}
			if (keepOpen) {
				setIsConfirming(false);
			} else {
				onHide(true);
			}
		}
	};

	const addDisabled = segments.length >= maxSegments;
	const editStepTitle = isEditing ? 'Edit Split shift' : 'Split shift';

	let confirmStepTitle = isEditing ? 'Confirm changes to Split shift' : 'Confirm Split shift';
	let confirmButtonLabel = isEditing ? 'Confirm changes' : 'Confirm Split';
	// PC-10: every remaining segment cancelled cancels the slot itself, so the confirmation step
	// announces that outcome instead of the ordinary split confirmation.
	if (willCancelSlot) {
		confirmStepTitle = 'Cancel shift?';
		confirmButtonLabel = 'Cancel shift';
	}
	const modalTitle = isConfirming ? confirmStepTitle : editStepTitle;
	const submitButtonLabel = isEditing ? 'Save changes' : 'Split shift';
	const submitDisabled = (isEditing && !hasChanges) || isLoading || isLoadingSegments;
	const shiftTimeLabel = isEditing
		? `${slot.shift_time_label} (${slot.segments_count} Segments)`
		: slot.shift_time_label;
	const showAddSegment = !unitsOnly;
	// Unit-only mode keeps Mark as Cancelled locked regardless of the facility permission.
	const canCancelSegments = canCancel && !unitsOnly;
	const isReverting = isEditing && sortedSegments.length === 1;

	const renderTimeCell = (row) => {
		if (row.status === 'removed' && row.wasCancelled) {
			return <span className="split-shift-diff-removed split-shift-diff-cancelled">CANCELLED</span>;
		}
		if (row.status === 'removed') {
			return <span className="split-shift-diff-removed">{row.previousTime}</span>;
		}
		// Two rows carry a bare CANCELLED with no struck time. Cancelled now and cancelled before this
		// edit too: nothing was cancelled here, so the row must not borrow the struck-time arrow
		// treatment that reads as "being cancelled now" — PC-13's "Unchanged" row governs, and
		// buildDiffRows agrees that cancellationChanged is false. And PC-13's first row: a segment
		// cancelled before it was ever saved (status 'added' is set exactly when buildDiffRows found no
		// stored counterpart) has no prior time to strike.
		if (row.isCancelled && (row.wasCancelled || row.status === 'added')) {
			return <span className="split-shift-diff-cancelled">CANCELLED</span>;
		}
		// PC-13 row 2: a stored segment newly marked cancelled. buildDiffRows only fills previousTime
		// when the time itself changed, so an otherwise-untouched segment falls back to row.time —
		// the reader still sees which time is being cancelled.
		if (row.isCancelled) {
			return (
				<>
					<span className="split-shift-diff-previous">{row.previousTime || row.time}</span>
					<span className="split-shift-diff-arrow">
						<ArrowIcon />
					</span>
					<span className="split-shift-diff-cancelled">CANCELLED</span>
				</>
			);
		}
		if (row.wasCancelled) {
			return (
				<>
					<span className="split-shift-diff-previous split-shift-diff-cancelled">CANCELLED</span>
					<span className="split-shift-diff-arrow">
						<ArrowIcon />
					</span>
					<span className="split-shift-diff-current">{row.time}</span>
				</>
			);
		}
		if (row.previousTime) {
			return (
				<>
					<span className="split-shift-diff-previous">{row.previousTime}</span>
					<span className="split-shift-diff-arrow">
						<ArrowIcon />
					</span>
					<span className="split-shift-diff-current">{row.time}</span>
				</>
			);
		}
		return <span className={row.status === 'added' ? 'split-shift-diff-current' : ''}>{row.time}</span>;
	};

	const renderUnitCell = (row) => {
		const hasNoPreviousUnit = row.previousUnit === NO_UNIT_LABEL;

		if (row.status === 'removed') {
			const removedClassName = hasNoPreviousUnit ? '' : 'split-shift-diff-removed';
			return <span className={removedClassName}>{row.previousUnit}</span>;
		}
		if (row.previousUnit) {
			const previousClassName = hasNoPreviousUnit
				? 'split-shift-diff-previous split-shift-diff-previous--no-strike'
				: 'split-shift-diff-previous';
			return (
				<>
					<span className={previousClassName}>{row.previousUnit}</span>
					<span className="split-shift-diff-arrow">
						<ArrowIcon />
					</span>
					<span className="split-shift-diff-current">{row.unit}</span>
				</>
			);
		}
		return <span className={row.status === 'added' ? 'split-shift-diff-current' : ''}>{row.unit}</span>;
	};

	const updatesTableBody = isEditing
		? diffRows.map((row) => (
				<tr key={`diff-segment-${row.segmentNumber}`} className={`split-shift-diff-${row.status}`}>
					<td>{row.segmentNumber}</td>
					<td>{renderTimeCell(row)}</td>
					{shouldShowUnits && <td>{renderUnitCell(row)}</td>}
				</tr>
		  ))
		: confirmationRows.map((row) => (
				<tr key={`segment-${row.segmentNumber}`}>
					<td>{row.segmentNumber}</td>
					<td>{row.isCancelled ? <span className="split-shift-diff-cancelled">CANCELLED</span> : row.time}</td>
					{shouldShowUnits && <td>{row.unit}</td>}
				</tr>
		  ));

	const cancelAllBanner = willCancelSlot ? (
		<div className="split-shift-cancel-all-banner">
			<img src={InfoIconGray} alt="" />
			<span>By cancelling all remaining segments, this shift will be marked as cancelled.</span>
		</div>
	) : null;

	const revertBanner = isReverting ? (
		<div className="split-shift-revert-banner">
			<img src={InfoIconGray} alt="" />
			<span>By removing all other segments, this will no longer be a split shift.</span>
		</div>
	) : null;

	const updatesSection = (
		<>
			<h2>Updates</h2>
			<table className="split-shift-updates">
				<thead>
					<tr>
						<th>Segment</th>
						<th>Time</th>
						{shouldShowUnits && <th>Unit</th>}
					</tr>
				</thead>
				<tbody>{updatesTableBody}</tbody>
			</table>
		</>
	);

	const segmentsSection = isLoadingSegments ? (
		<LoadingSpinner />
	) : (
		<>
			<h2>Segments</h2>
			<div className="split-shift-segments">
				{segments.map((segment, index) => (
					<SplitShiftSegmentRow
						key={segment.rowId}
						index={index}
						number={index + 1}
						segment={segment}
						shiftStart={shiftStart}
						shiftEnd={shiftEnd}
						isFirst={index === 0}
						isLast={index === segments.length - 1}
						canDelete={!unitsOnly && canDeleteSegment(index, segments.length, isEditing)}
						timesDisabled={unitsOnly}
						unitOptions={unitOptions}
						startError={errors[`segments[${index}].startTime`]}
						endError={errors[`segments[${index}].endTime`]}
						onStartChange={handleStartChange}
						onEndChange={handleEndChange}
						onStartError={handleStartError}
						onEndError={handleEndError}
						onUnitChange={handleUnitChange}
						onDelete={handleDeleteSegment}
						onCancelToggle={handleCancelToggle}
						cancelationReasonOptions={cancelationReasonOptions}
						cancelationReasonError={errors[`segments[${index}].cancelationReason`]}
						onCancelationReasonChange={handleCancelationReasonChange}
						canCancel={canCancelSegments}
						shouldShowUnits={shouldShowUnits}
					/>
				))}
			</div>
			{showAddSegment && (
				<button
					type="button"
					className="btn-light-purple split-shift-add"
					onClick={handleAddSegment}
					disabled={addDisabled}
				>
					{' '}
					<img src={addDisabled ? ArrowSplitIconWhite : ArrowSplitIconPurple} alt="Add Segment" />
					Add segment ({segments.length}/{maxSegments})
				</button>
			)}
		</>
	);

	return (
		<Modal
			size="lg"
			centered
			show={show}
			onHide={onHide}
			className="split-shift-modal"
			backdrop="static"
			keyboard={false}
		>
			<Modal.Header closeButton>
				<Modal.Title>{modalTitle}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{isConfirming && (willCancelSlot ? cancelAllBanner : revertBanner)}
				<h2>Current</h2>
				<div className="split-shift-current">
					<div className="split-shift-current-col">
						<div>
							Shift type: <b>{slot.shift_type || '-'}</b>
						</div>
						<div>
							Date: <b>{slot.shift_date_label || '-'}</b>
						</div>
						<div>
							Shift time: <b>{shiftTimeLabel || '-'}</b>
						</div>
					</div>
					<div className="split-shift-current-col">
						<div>
							Professional: <b>{slot.professional_name || '-'}</b>
						</div>
						<div>
							Facility: <b>{slot.facility_name || '-'}</b>
						</div>
					</div>
				</div>
				{isConfirming ? updatesSection : segmentsSection}
			</Modal.Body>
			<Modal.Footer>
				{isConfirming ? (
					<>
						<button type="button" className="btn-light" onClick={handleGoBack}>
							Go back
						</button>
						<button
							type="button"
							className={classNames('btn-purple', { 'split-shift-cancel-confirm': willCancelSlot })}
							onClick={handleConfirm}
							disabled={isLoading}
						>
							{confirmButtonLabel}
						</button>
					</>
				) : (
					<>
						<button type="button" className="btn-light" onClick={onHide}>
							Cancel
						</button>
						<button type="button" className="btn-purple" onClick={handleSplit} disabled={submitDisabled}>
							{submitButtonLabel}
						</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default memo(SplitShiftModal);
